import React, { useState, useEffect } from 'react';
import { systemAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { 
  Activity, Search, Filter, Calendar, MapPin, Monitor, 
  ChevronLeft, ChevronRight, User, Shield, Info, Database, Settings, Save
} from 'lucide-react';

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [filters, setFilters] = useState({ action: '', user_id: '' });
  const [settings, setSettings] = useState({ user_limit: 10 });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchSettings();
  }, [page, filters]);

  const fetchSettings = async () => {
    try {
      const res = await systemAPI.settings();
      if (res.data.settings) setSettings(prev => ({ ...prev, ...res.data.settings }));
    } catch (err) {
      console.error('Failed to fetch settings');
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await systemAPI.updateSettings({ settings });
      toast.success('System settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await systemAPI.auditLogs({
        limit,
        offset: page * limit,
        ...filters
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'LOGIN': return '#10B981';
      case 'LOGOUT': return '#64748B';
      case 'DELETE_USER':
      case 'DELETE_JOB': return '#EF4444';
      case 'UPDATE_JOB':
      case 'UPDATE_ASSET': return '#0EA5E9';
      default: return '#8B5CF6';
    }
  };

  return (
    <div className="fade-in">
      {/* Stats Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(14,165,233,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Activity color="#0EA5E9" size={24} />
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{total}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Total Events Tracked</div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div className="search-box" style={{ flex: 1, minWidth: 250 }}>
            <Search size={16} />
            <input 
              type="text" 
              placeholder="Filter by Action (e.g. LOGIN)..." 
              value={filters.action}
              onChange={e => setFilters(f => ({ ...f, action: e.target.value.toUpperCase() }))}
            />
          </div>
          <button className="btn btn-secondary" onClick={() => { setFilters({ action:'', user_id:'' }); setPage(0); }}>
            Reset Filters
          </button>
        </div>
      </div>

      {/* System Settings Card */}
      <div className="card" style={{ marginBottom: 24, border: '1px solid var(--accent-muted)', background: 'linear-gradient(to right, var(--bg-card), var(--bg-body))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 10, background: 'var(--accent)', borderRadius: 12, color: '#fff' }}>
            <Settings size={20} />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>System Configuration</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Global platform constraints and limits</p>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24, alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              <User size={14} /> Maximum User Capacity
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <input 
                type="number" 
                className="input" 
                value={settings.user_limit} 
                onChange={e => setSettings({ ...settings, user_limit: parseInt(e.target.value) })}
                style={{ width: 120 }}
              />
              <button 
                className="btn btn-primary" 
                onClick={handleSaveSettings}
                disabled={savingSettings}
                style={{ gap: 8 }}
              >
                {savingSettings ? 'Saving...' : <><Save size={16} /> Save Limit</>}
              </button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              Current user count will be checked against this limit during new user registration.
            </p>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="card overflow-hidden">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>IP & Device</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ textAlign:'center', padding:40 }}>Loading logs...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign:'center', padding:40 }}>No logs found</td></tr>
              ) : logs.map(log => (
                <tr key={log.id}>
                  <td style={{ whiteSpace:'nowrap' }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{formatDate(log.created_at)}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(log.created_at).getTime()}</div>
                  </td>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div className="avatar avatar-sm" style={{ background:'var(--bg-muted)', color:'var(--text-secondary)' }}>
                        <User size={14} />
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600 }}>{log.user_name || 'System'}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{log.user_email || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ 
                      padding:'4px 10px', borderRadius:'6px', fontSize:11, fontWeight:700,
                      background: `${getActionColor(log.action)}15`,
                      color: getActionColor(log.action),
                      border: `1px solid ${getActionColor(log.action)}30`
                    }}>
                      {log.action}
                    </span>
                  </td>
                  <td>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <MapPin size={12} color="var(--text-muted)" />
                        {log.ip_address || 'Unknown IP'}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'var(--text-muted)', maxWidth: 200 }} className="truncate" title={log.user_agent}>
                        <Monitor size={12} />
                        {log.user_agent}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize:12, color:'var(--text-secondary)', maxWidth: 250 }}>
                      {log.entity_type && (
                        <span style={{ fontWeight:700, color:'var(--text-muted)', marginRight:4 }}>
                          [{log.entity_type}]
                        </span>
                      )}
                      {Object.keys(log.details || {}).length > 0 ? JSON.stringify(log.details) : 'No extra details'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ padding:'16px 24px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:13, color:'var(--text-muted)' }}>
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} entries
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button 
              className="btn-icon" 
              disabled={page === 0} 
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              className="btn-icon" 
              disabled={(page + 1) * limit >= total} 
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
